import resolveLocaleRoute from "@/Helpers/resolveLocaleRoute";
import resolveTaxonomy from "@/Helpers/resolveTaxonomy";
import FrontLayout from "@/Layouts/System/FrontLayout";
import SINGLE_POST_ACF_BLOCKS from "@/Query/Fragments/SINGLE_POST_ACF_BLOCKS";
import { Head } from "@inertiajs/react";
import Container from "@/Components/Grid/Container";
import Row from "@/Components/Grid/Row";
import Column from "@/Components/Grid/Column";
import LargeSkeleton from "@/Components/Preloaders/LargeSkeleton";
import GraphSrcSetImage from "@/Components/Front/Blocks/GraphSrcSetImage/GraphSrcSetImage";
import { Icon } from "@iconify/react";
import FrontText from "@/Components/Front/FrontText";
import AcfBlocksResolver from "@/Helpers/AcfBlocksResolver";
import { useTranslation } from "react-i18next";
import sanitizeHTML from "@/Helpers/sanitizeHTML";

import gql from 'graphql-tag';
import { useQuery } from "@apollo/client/react/hooks/hooks.cjs";
import FrontNewsCta1 from "@/Components/Front/Blocks/FrontNewsCta1";

const NewsSingle = ({ taxonomy, slug, canonical }) => {
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.resolvedLanguage.toUpperCase()
    const isServer = typeof window === 'undefined'
    
    const POST_CONTENT = gql`
        query GetPost($slug: ID!, $lang: LanguageCodeEnum!) {
            post(
                id: $slug
                idType: SLUG
            ) {
                title
                date
                translation(language: $lang) {
                    slug
                }
                featuredImage {
                    node {
                      srcSet
                    }
                }
                seo {
                    metaDesc
                    metaKeywords
                    opengraphDescription
                    opengraphPublishedTime
                    readingTime
                    opengraphType
                    opengraphTitle
                    opengraphImage {
                        mediaItemUrl
                    }
                    twitterTitle
                    twitterDescription
                    twitterImage {
                        mediaItemUrl
                    }
                    focuskw
                }
                ${SINGLE_POST_ACF_BLOCKS}
            }
        }
    `;
    
    

    const { loading, error, data } = useQuery(POST_CONTENT, {
        variables: { slug: slug, lang: currentLanguage === 'PL' ? 'EN' : 'PL' },
        ssr: true,
        fetchPolicy: 'cache-and-network',
    });

    if (loading || error || !data || !data.post) {
        return (
            <FrontLayout loadingBreadcrumbs={loading} breadcrumbs={[
                { title: t('breadcrumbs.news').toLowerCase(), href: resolveLocaleRoute('front.news', null, i18n.resolvedLanguage) },
                { title: resolveTaxonomy(taxonomy).toLocaleLowerCase(), href: resolveLocaleRoute('front.news.loop', { taxonomy }, i18n.resolvedLanguage) },
                { title: 'Ładowanie...', current: true }
            ]}>
                <img src="/storage/images/blog-bg-hero.svg" className="w-full h-[490px]" />
                <Container>
                    <Row>
                        <Column xl={8} lg={8} md={10} className='mx-auto'>
                            <div className="-mt-[45%]">
                                <LargeSkeleton />
                            </div>
                        </Column>
                    </Row>
                </Container>
            </FrontLayout>
        )
    }

    if (data && data.post && data.post.seo) {
        const {
            metaDesc,
            metaKeywords,
            opengraphDescription,
            opengraphPublishedTime,
            readingTime,
            opengraphType,
            opengraphTitle,
            opengraphImage,
            twitterTitle,
            twitterDescription,
            twitterImage,
            focuskw,
        } = data.post.seo

        

        const schemaData = {
            "@context": "http://schema.org",
            "@type": "NewsArticle",
            "headline": data.post.title,
            "description": metaDesc,
            "image": data.post.featuredImage?.mediaItemUrl,
            "datePublished": opengraphPublishedTime,
            "author": {
                "@type": "Organization",
                "name": "Warehousemarket.pl"
            },
            "publisher": {
                "@type": "Organization",
                "name": "Warehousemarket.pl",
                "logo": {
                    "@type": "ImageObject",
                    "url": `https://warehousemarket.pl/storage/images/front-logo.png`
                }
            }
        };

        return (
            <FrontLayout loadingBreadcrumbs={loading} breadcrumbs={[
                { title: t('breadcrumbs.news').toLowerCase(), href: resolveLocaleRoute('front.news', null, i18n.resolvedLanguage) },
                { title: resolveTaxonomy(taxonomy).toLocaleLowerCase(), href: resolveLocaleRoute('front.news.loop', { taxonomy }, i18n.resolvedLanguage) },
                { title: data?.post?.title, current: true }
            ]}>
                <>
                    <Head>
                        <meta name="description" content={metaDesc} />
                        <meta name="keywords" content={metaKeywords} />

                        <meta property="og:title" content={opengraphTitle} />
                        <meta property="og:description" content={`${opengraphDescription} - czas czytania ok. ${readingTime} min`} />
                        <meta property="og:type" content={opengraphType} />
                        <meta property="og:url" content={canonical} />
                        <meta property="og:image" content={opengraphImage ? opengraphImage.mediaItemUrl : data.post.featuredImage?.mediaItemUrl} />
                        <meta property="og:published_time" content={opengraphPublishedTime} />

                        <meta name="twitter:title" content={twitterTitle} />
                        <meta name="twitter:description" content={twitterDescription} />
                        <meta name="twitter:image" content={twitterImage ? twitterImage.mediaItemUrl : data.post.featuredImage?.mediaItemUrl} />

                        <meta name="focuskw" content={focuskw} />
                        {!isServer &&
                            <link rel="canonical" href={canonical} />
                        }

                        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />

                        <title>{opengraphTitle ? opengraphTitle : data.post.title}</title>
                    </Head>
                    <article className="relative bg-no-repeat bg-top lg:bg-[length:120vw] md:bg-[length:130vw] bg-[length:200vw] xl:bg-auto single-article" style={{ backgroundImage: 'url(/storage/images/blog-bg-hero.svg)' }}>
                        <Container>
                            <Row>
                                <Column xl={8} lg={8} md={10} className={'mx-auto lg:mt-20 md:mt-15 mt-10 relative'}>
                                    <GraphSrcSetImage alt={data.post.title} srcSet={data.post.featuredImage.node.srcSet} className="max-w-[860px] w-full h-auto object-cover rounded-10 md:mb-6 mb-1" />
                                    <div className="flex items-center xl:mb-20 lg:mb-15 md:mb-10 mb-5">
                                        <Icon icon='material-symbols:menu-book-rounded' width={50} className="text-30548A mr-4 md:w-[50px] w-8" />
                                        <FrontText textSize='20/24-m-16/22' className="font-bold font-domine text-30548A">
                                            <span>
                                                {new Date(data.post.date).toLocaleDateString(currentLanguage, { day: 'numeric', month: 'long', year: 'numeric' })}
                                            </span>
                                        </FrontText>
                                    </div>
                                    <FrontText textSize='32/36-m-20/23' className="font-bold font-domine text-center mb-10">
                                        <h1>{data.post.title}</h1>
                                    </FrontText>
                                    <FrontNewsCta1 className="absolute left-full hidden 2xl:block 2xl:ml-[calc((100vw_-_882px)_/_8)]" />
                                </Column>
                                {data.post.blocks && data.post.blocks.map((block, id) => {
                                    if(block.name.startsWith('acf/')){
                                        return(
                                            <AcfBlocksResolver key={block.name + id} block={block} id={id}/>
                                        )
                                    }
                                    return (
                                        <Column key={block.name+id} xl={8} lg={8} md={10} className={'mx-auto standard-editor'}>
                                            <div>
                                                {sanitizeHTML(block.originalContent)}
                                            </div>
                                        </Column>
                                    )
                                })}
                            </Row>
                        </Container>
                    </article>
                </>
            </FrontLayout>
        )
    }
}

export default NewsSingle;